import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiFolder, 
  FiFileText, 
  FiPlus, 
  FiDownload, 
  FiUpload, 
  FiChevronRight, 
  FiMoreVertical,
  FiArrowLeft,
  FiTrash2,
  FiImage,
  FiFile
} = FiIcons;

const FolderManagement = () => {
  const [currentPath, setCurrentPath] = useState([]);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  
  // Mock File System
  const [items, setItems] = useState([
    { id: 1, name: 'Regulatory Docs', type: 'folder', parentId: null, date: '2025-11-10' },
    { id: 2, name: 'Marketing Materials', type: 'folder', parentId: null, date: '2025-11-12' },
    { id: 3, name: 'Package Insert.pdf', type: 'file', parentId: null, size: '2.4 MB', date: '2025-11-15' },
    { id: 4, name: 'FDA Approval Letter.pdf', type: 'file', parentId: 1, size: '1.8 MB', date: '2025-11-10' },
    { id: 5, name: 'Product Brochure.pdf', type: 'file', parentId: 2, size: '5.2 MB', date: '2025-11-12' },
    { id: 6, name: 'Label Design.ai', type: 'file', parentId: 2, size: '12.5 MB', date: '2025-11-12' },
  ]);

  const getCurrentItems = () => {
    const currentFolderId = currentPath.length > 0 ? currentPath[currentPath.length - 1].id : null;
    return items.filter(item => item.parentId === currentFolderId);
  };

  const handleCreateFolder = (e) => {
    e.preventDefault();
    if (newFolderName.trim()) {
      const parentId = currentPath.length > 0 ? currentPath[currentPath.length - 1].id : null;
      const newFolder = {
        id: Date.now(),
        name: newFolderName,
        type: 'folder',
        parentId,
        date: new Date().toISOString().split('T')[0]
      };
      setItems([...items, newFolder]);
      setNewFolderName('');
      setIsCreatingFolder(false);
    }
  };

  const handleNavigate = (folder) => {
    setCurrentPath([...currentPath, folder]);
  };

  const handleNavigateUp = () => {
    setCurrentPath(currentPath.slice(0, -1));
  };

  const handleBreadcrumbClick = (index) => {
    setCurrentPath(currentPath.slice(0, index + 1));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const parentId = currentPath.length > 0 ? currentPath[currentPath.length - 1].id : null;
      const newFile = {
        id: Date.now(),
        name: file.name,
        type: 'file',
        parentId,
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        date: new Date().toISOString().split('T')[0]
      };
      setItems([...items, newFile]);
    }
  };

  const handleDelete = (id) => {
    // Simple delete, in a real app would need to delete children recursively
    setItems(items.filter(item => item.id !== id));
  };

  const getIcon = (type, name) => {
    if (type === 'folder') return <SafeIcon icon={FiFolder} className="text-blue-500 fill-blue-100" />;
    if (name.endsWith('.pdf')) return <SafeIcon icon={FiFileText} className="text-red-500" />;
    if (name.endsWith('.png') || name.endsWith('.jpg')) return <SafeIcon icon={FiImage} className="text-purple-500" />;
    return <SafeIcon icon={FiFile} className="text-gray-500" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <SafeIcon icon={FiFolder} className="text-blue-600" />
          Documents
        </h3>
        <div className="flex gap-2">
          <button 
            onClick={() => setIsCreatingFolder(true)}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-gray-200"
            title="New Folder"
          >
            <SafeIcon icon={FiPlus} />
          </button>
          <label className="p-2 text-gray-600 hover:text-blue-600 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-gray-200 cursor-pointer" title="Upload File">
            <SafeIcon icon={FiUpload} />
            <input type="file" className="hidden" onChange={handleFileUpload} />
          </label>
        </div>
      </div>

      {/* Breadcrumbs & Navigation */}
      <div className="p-3 border-b border-gray-100 flex items-center gap-2 text-sm overflow-x-auto">
        {currentPath.length > 0 ? (
          <button onClick={() => setCurrentPath([])} className="p-1 hover:bg-gray-100 rounded text-gray-500">
            <SafeIcon icon={FiArrowLeft} />
          </button>
        ) : null}
        
        <button 
          onClick={() => setCurrentPath([])}
          className={`hover:text-blue-600 ${currentPath.length === 0 ? 'font-semibold text-gray-900' : 'text-gray-500'}`}
        >
          Root
        </button>
        
        {currentPath.map((folder, index) => (
          <React.Fragment key={folder.id}>
            <SafeIcon icon={FiChevronRight} className="text-gray-400 flex-shrink-0" />
            <button
              onClick={() => handleBreadcrumbClick(index)}
              className={`hover:text-blue-600 whitespace-nowrap ${
                index === currentPath.length - 1 ? 'font-semibold text-gray-900' : 'text-gray-500'
              }`}
            >
              {folder.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* New Folder Input */}
      <AnimatePresence>
        {isCreatingFolder && (
          <motion.form
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            onSubmit={handleCreateFolder}
            className="p-3 border-b border-gray-100 bg-blue-50"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name..."
                className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <button
                type="submit"
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => setIsCreatingFolder(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-blue-100 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* File List */}
      <div className="max-h-[300px] overflow-y-auto">
        {getCurrentItems().length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <SafeIcon icon={FiFolder} className="text-4xl mx-auto mb-2 text-gray-300" />
            <p>This folder is empty</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {getCurrentItems().map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 hover:bg-gray-50 group transition-colors"
                onDoubleClick={() => item.type === 'folder' && handleNavigate(item)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="text-xl flex-shrink-0">
                    {getIcon(item.type, item.name)}
                  </div>
                  <div className="min-w-0">
                    <p 
                      className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-blue-600"
                      onClick={() => item.type === 'folder' && handleNavigate(item)}
                    >
                      {item.name}
                    </p>
                    <p className="text-xs text-gray-500 flex gap-2">
                      <span>{item.date}</span>
                      {item.size && <span>• {item.size}</span>}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.type === 'file' && (
                    <button className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Download">
                      <SafeIcon icon={FiDownload} />
                    </button>
                  )}
                  <button 
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" 
                    title="Delete"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default FolderManagement;